import {
  Anchor,
  AspectRatio,
  Flex,
  Group,
  Image,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import excellentDark from "../../../../../assets/illustrations/conditions/excellent-dark.svg";
import excellent from "../../../../../assets/illustrations/conditions/excellent.svg";
import fairDark from "../../../../../assets/illustrations/conditions/fair-dark.svg";
import fair from "../../../../../assets/illustrations/conditions/fair.svg";
import goodDark from "../../../../../assets/illustrations/conditions/good-dark.svg";
import good from "../../../../../assets/illustrations/conditions/good.svg";
import superbDark from "../../../../../assets/illustrations/conditions/superb-dark.svg";
import superb from "../../../../../assets/illustrations/conditions/superb.svg";

import { TransactionStatusBadge } from "@/components/elements";
import { IconCheck, IconInfo } from "@/components/icons";
import { paths } from "@/config/paths";
import { defaultCurrency } from "@/lib/currency";
import { formatDate } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";
import {
  getListingPriceLabel,
  getTransactionNotAvailableLabel,
  shouldHideTransactionPrice,
} from "@/utils/transactionRecord";

import type { ListTableRowProps } from "@/components/elements";
import type { AuctionCarTransaction, AuctionCarTransactionColumns } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { FC, ReactNode } from "react";

export type TransactionTableRowProps = ListTableRowProps<
  AuctionCarTransaction,
  AuctionCarTransactionColumns
> & {
  onMoreClick: () => void;
};

export const TransactionTableRow: FC<TransactionTableRowProps> = ({
  columns,
  data,
  onMoreClick,
  ...props
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  function renderPrice(value?: number) {
    if (shouldHideTransactionPrice(data, value)) {
      return (
        <Text fz="sm" c=".gray-4">
          {getTransactionNotAvailableLabel(t)}
        </Text>
      );
    }

    return formatCurrency(value!, currency.code);
  }

  function renderCell(column: AuctionCarTransactionColumns) {
    try {
      switch (column) {
        case "date":
          return (
            <Text component="span" fz="sm" style={{ whiteSpace: "nowrap" }}>
              {formatDate(data[column])}
            </Text>
          );
        case "more":
          return (
            hasMoreInfo(data[column]) && (
              <Anchor
                component="button"
                onClick={onMoreClick}
                size="sm"
                style={{
                  textWrap: "nowrap",
                }}
              >
                {t("components.TransactionTableRow.see_more")}
              </Anchor>
            )
          );
        case "listing_price": {
          const listingPriceLabel = getListingPriceLabel(t, data, data[column]);

          if (listingPriceLabel) {
            return (
              <Text fz="sm" c=".gray-4">
                {listingPriceLabel}
              </Text>
            );
          }

          return formatCurrency(data[column], currency.code);
        }
        case "hammer_price":
        case "net_to_seller":
          return renderPrice(data[column]);
        case "event_region":
        case "event_country": {
          const value = data[column];

          if (value?.trim()) {
            return (
              <Text
                fz="sm"
                style={{
                  textWrap: "nowrap",
                }}
              >
                {value}
              </Text>
            );
          }

          return (
            <Text
              fz="sm"
              c=".gray-4"
              style={{
                textWrap: "nowrap",
              }}
            >
              {getTransactionNotAvailableLabel(t)}
            </Text>
          );
        }
        case "price":
          if (shouldHideTransactionPrice(data, data[column])) {
            return renderPrice(data[column]);
          }

          return (
            <Group gap="xs">
              {formatCurrency(data[column], currency.code)}
              <Tooltip
                label={t("components.TransactionTableRow.advertised_price")}
                events={{ hover: true, focus: true, touch: true }}
              >
                <ThemeIcon variant="transparent" color="primary" size="xs">
                  <IconInfo size={16} fill="var(--mantine-color-secondary-4)" />
                </ThemeIcon>
              </Tooltip>
            </Group>
          );
        case "result":
          return (
            <Text component="span" fz="sm" style={{ whiteSpace: "nowrap" }}>
              {t(`components.TransactionTableRow.result.${data[column]}`, data[column])}
            </Text>
          );
        case "condition":
        case "type": {
          const value = data[column];
          const cond = String(value).toLowerCase();

          // explicit handling for unavailable condition: show translated text
          if (cond === "not_available") {
            return (
              <Text fz="sm" style={{ flex: 1, minWidth: 0 }}>
                {t(`car.conditions.not_available`, "not_available")}
              </Text>
            );
          }

          const imgSrc =
            cond === "superb"
              ? isDark
                ? superbDark
                : superb
              : cond === "excellent"
                ? isDark
                  ? excellentDark
                  : excellent
                : cond === "good"
                  ? isDark
                    ? goodDark
                    : good
                  : cond === "fair"
                    ? isDark
                      ? fairDark
                      : fair
                    : undefined;

          if (!imgSrc) {
            return (
              <Text fz="sm" tt="uppercase" variant="light">
                {t(`car.conditions.${value}`, value)}
              </Text>
            );
          }

          return (
            <Flex direction="row" justify="space-between" align="center" gap="md" w="fit">
              <Text fz="sm" miw={0} style={{ flex: 1 }} truncate>
                {t(`car.conditions.${data.condition}`, data.condition)}
              </Text>
              <AspectRatio
                w="2em"
                style={{ display: "flex", alignItems: "center" }}
                pos="relative"
                bottom={2}
              >
                <Image
                  src={imgSrc}
                  fit="contain"
                  alt={t(`car.conditions.${cond}`, cond)}
                  aria-label={t(`car.conditions.${cond}`, cond)}
                  style={{ display: "block", margin: "auto" }}
                />
              </AspectRatio>
            </Flex>
          );
        }
        case "outlier":
          return data.outlier ? (
            <ThemeIcon variant="transparent" color="success" size="sm">
              <IconCheck size={16} />
            </ThemeIcon>
          ) : null;
        case "replica":
          return data.replica ? (
            <ThemeIcon variant="transparent" color="success" size="sm">
              <IconCheck size={16} />
            </ThemeIcon>
          ) : null;
        case "status":
          return <TransactionStatusBadge status={data[column]} size="sm" />;
        case "vin":
          return (
            <Anchor component={Link} to={paths.app.vin.car.getHref(data[column])} size="sm">
              {data[column]}
            </Anchor>
          );
        default:
          return data[column] as ReactNode;
      }
    } catch (err) {
      console.error("Error:", err);
      return null;
    }
  }

  return (
    <Table.Tr {...props}>
      {columns.map((column) => (
        <Table.Td
          key={column}
          style={
            column === "condition"
              ? undefined
              : {
                  whiteSpace: "nowrap",
                  width: "1%",
                }
          }
        >
          {renderCell(column)}
        </Table.Td>
      ))}
    </Table.Tr>
  );
};

function hasMoreInfo(data?: AuctionCarTransaction["more"]) {
  return Boolean(data?.description || data?.image || data?.website);
}
